import { useState, useEffect } from 'react';
import { Globe, Plus, Trash2, TestTube, CheckCircle, XCircle, Lock, Webhook, MessageSquare, Database } from 'lucide-react';
import { useNotifications } from './NotificationSystem';
import { API_BASE_URL } from '../services/api';

const FlowIntegrations = ({ flow, onClose }) => {
  const { showSuccess, showError } = useNotifications();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    type: 'webhook',
    url: '',
    method: 'POST',
    headers: {},
    authType: 'none',
    apiKey: '',
    bearerToken: '',
    enabled: true,
  });

  const integrationTypes = [
    { id: 'webhook', label: 'Webhook', icon: Webhook, description: 'Generic HTTP webhook' },
    { id: 'slack', label: 'Slack', icon: MessageSquare, description: 'Slack webhook integration' },
    { id: 'discord', label: 'Discord', icon: MessageSquare, description: 'Discord webhook integration' },
    { id: 'zapier', label: 'Zapier', icon: Globe, description: 'Zapier webhook integration' },
    { id: 'make', label: 'Make.com', icon: Globe, description: 'Make.com webhook integration' },
    { id: 'custom', label: 'Custom API', icon: Database, description: 'Custom API endpoint' },
  ];

  useEffect(() => {
    loadIntegrations();
  }, [flow]);

  const flattenIntegration = (i) => ({ ...i, ...(i.config || {}) });

  const loadIntegrations = async () => {
    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');
      const response = await fetch(`${base}/integrations?flow_id=${flow?.id || 'current'}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });

      if (response.ok) {
        const data = await response.json();
        // Worker returns array directly; flatten nested config into the object
        setIntegrations(Array.isArray(data) ? data.map(flattenIntegration) : []);
      } else {
        const saved = localStorage.getItem('ai-flow-integrations');
        if (saved) setIntegrations(JSON.parse(saved));
      }
    } catch (error) {
      console.warn('Failed to load integrations from backend, using localStorage:', error);
      const saved = localStorage.getItem('ai-flow-integrations');
      if (saved) setIntegrations(JSON.parse(saved));
    }
  };

  const validateUrl = (url) => {
    if (!url) return false;
    try {
      const parsed = new URL(url);
      // Security: Block localhost and private IPs
      const blockedHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
      const hostname = parsed.hostname.toLowerCase();
      
      if (blockedHosts.includes(hostname) || 
          hostname.startsWith('192.168.') ||
          hostname.startsWith('10.') ||
          /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
        return false;
      }
      
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  const handleAddIntegration = async () => {
    if (!newIntegration.name.trim()) {
      showError('Please enter an integration name', 'Validation Error');
      return;
    }

    if (!validateUrl(newIntegration.url)) {
      showError('Invalid URL. Please use a valid HTTP/HTTPS URL (localhost and private IPs are blocked for security).', 'URL Validation Error');
      return;
    }

    setLoading(true);
    try {
      // Worker expects {name, type, config: {...}, enabled, flow_id}
      const payload = {
        name: newIntegration.name,
        type: newIntegration.type,
        enabled: newIntegration.enabled,
        flow_id: flow?.id || 'current',
        config: {
          url: newIntegration.url,
          method: newIntegration.method,
          authType: newIntegration.authType,
          apiKey: newIntegration.apiKey,
          bearerToken: newIntegration.bearerToken,
          headers: newIntegration.headers,
        },
      };

      let saved = null;
      try {
        const base = (API_BASE_URL || '/api').replace(/\/$/, '');
        const response = await fetch(`${base}/integrations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
        });

        if (response.ok) {
          saved = await response.json();
        } else {
          throw new Error('Backend save failed');
        }
      } catch (backendError) {
        console.warn('Backend save failed, using localStorage:', backendError);
        const localList = JSON.parse(localStorage.getItem('ai-flow-integrations') || '[]');
        saved = { id: `int_${Date.now()}`, ...payload };
        localList.push(saved);
        localStorage.setItem('ai-flow-integrations', JSON.stringify(localList));
      }

      // Flatten config so component state stays in flat shape
      const integration = { ...saved, ...(saved.config || {}) };
      setIntegrations(prev => [...prev, integration]);
      setNewIntegration({
        name: '',
        type: 'webhook',
        url: '',
        method: 'POST',
        headers: {},
        authType: 'none',
        apiKey: '',
        bearerToken: '',
        enabled: true,
      });
      setShowAddModal(false);
      showSuccess('Integration added successfully!', 'Integration Created');
    } catch (error) {
      console.error('Failed to add integration:', error);
      showError('Failed to add integration. Please try again.', 'Integration Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIntegration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      // Delete from backend
      try {
        const base = (API_BASE_URL || '/api').replace(/\/$/, '');
        await fetch(`${base}/integrations/${id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        });
      } catch (backendError) {
        // Fallback to localStorage
        const saved = localStorage.getItem('ai-flow-integrations') || '[]';
        const integrations = JSON.parse(saved).filter(i => i.id !== id);
        localStorage.setItem('ai-flow-integrations', JSON.stringify(integrations));
      }

      setIntegrations(prev => prev.filter(i => i.id !== id));
      showSuccess('Integration deleted successfully!', 'Integration Deleted');
    } catch (error) {
      console.error('Failed to delete integration:', error);
      showError('Failed to delete integration. Please try again.', 'Delete Error');
    }
  };

  const handleTestIntegration = async (integration) => {
    setTesting(prev => ({ ...prev, [integration.id]: true }));

    try {
      const testPayload = {
        test: true,
        timestamp: new Date().toISOString(),
        flow_id: flow?.id || 'current',
        message: 'Test integration from AI Flow',
      };

      const headers = {
        'Content-Type': 'application/json',
        ...integration.headers,
      };

      // Add authentication
      if (integration.authType === 'apiKey' && integration.apiKey) {
        headers['X-API-Key'] = integration.apiKey;
      } else if (integration.authType === 'bearer' && integration.bearerToken) {
        headers['Authorization'] = `Bearer ${integration.bearerToken}`;
      }

      const response = await fetch(integration.url, {
        method: integration.method || 'POST',
        headers,
        body: JSON.stringify(testPayload),
      });

      const success = response.ok;
      setTesting(prev => ({ ...prev, [integration.id]: success ? 'success' : 'error' }));

      setTimeout(() => {
        setTesting(prev => {
          const next = { ...prev };
          delete next[integration.id];
          return next;
        });
      }, 3000);
    } catch (error) {
      console.error('Integration test failed:', error);
      setTesting(prev => ({ ...prev, [integration.id]: 'error' }));
      setTimeout(() => {
        setTesting(prev => {
          const next = { ...prev };
          delete next[integration.id];
          return next;
        });
      }, 3000);
    }
  };

  const handleToggleIntegration = async (id) => {
    setIntegrations(prev => prev.map(i => 
      i.id === id ? { ...i, enabled: !i.enabled } : i
    ));

    try {
      const base = (API_BASE_URL || '/api').replace(/\/$/, '');
      const target = integrations.find(i => i.id === id);
      if (!target) return;
      await fetch(`${base}/integrations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: target.name,
          type: target.type,
          enabled: !target.enabled,
          config: {
            url: target.url,
            method: target.method,
            authType: target.authType,
            apiKey: target.apiKey,
            bearerToken: target.bearerToken,
            headers: target.headers,
          },
        }),
        credentials: 'same-origin',
      });
    } catch (error) {
      console.warn('Failed to update integration status:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Flow Integrations</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close integrations"
          >
            ×
          </button>
        </div>

        <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Security Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <Lock size={16} className="text-yellow-600 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-yellow-800">Security Notice</p>
                <p className="text-xs text-yellow-700 mt-1">
                  All webhook URLs are validated for security. Localhost and private IPs are blocked. 
                  API keys and tokens are encrypted and stored securely.
                </p>
              </div>
            </div>
          </div>

          {/* Add Integration Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mb-4"
          >
            <Plus size={16} />
            Add Integration
          </button>

          {/* Integrations List */}
          <div className="space-y-3">
            {integrations.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Globe size={48} className="mx-auto mb-2 text-gray-300" />
                <p>No integrations configured</p>
                <p className="text-sm">Add an integration to connect your flow to external services</p>
              </div>
            ) : (
              integrations.map(integration => {
                const typeInfo = integrationTypes.find(t => t.id === integration.type) || integrationTypes[0];
                const TypeIcon = typeInfo.icon;
                const testStatus = testing[integration.id];

                return (
                  <div
                    key={integration.id}
                    className={`border rounded-lg p-4 ${
                      integration.enabled
                        ? 'border-gray-200 bg-white'
                        : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <TypeIcon size={20} className="text-blue-600" />
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-800">{integration.name}</h4>
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {typeInfo.label}
                            </span>
                            {integration.enabled ? (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                Enabled
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{typeInfo.description}</p>
                          <p className="text-xs text-gray-400 mt-1 font-mono truncate max-w-md">
                            {integration.url}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleIntegration(integration.id)}
                          className={`text-xs px-2 py-1 rounded ${
                            integration.enabled
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } transition-colors`}
                        >
                          {integration.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleTestIntegration(integration)}
                          disabled={testStatus === true}
                          className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                        >
                          {testStatus === true ? (
                            <>
                              <TestTube size={12} className="animate-spin" />
                              Testing...
                            </>
                          ) : testStatus === 'success' ? (
                            <>
                              <CheckCircle size={12} />
                              Success
                            </>
                          ) : testStatus === 'error' ? (
                            <>
                              <XCircle size={12} />
                              Failed
                            </>
                          ) : (
                            <>
                              <TestTube size={12} />
                              Test
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteIntegration(integration.id)}
                          className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Add Integration</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Integration Name</label>
                <input
                  type="text"
                  value={newIntegration.name}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My Webhook Integration"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Integration Type</label>
                <select
                  value={newIntegration.type}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {integrationTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={newIntegration.url}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://hooks.slack.com/services/..."
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a valid HTTPS URL (localhost and private IPs are blocked for security)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">HTTP Method</label>
                <select
                  value={newIntegration.method}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, method: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="GET">GET</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authentication</label>
                <select
                  value={newIntegration.authType}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, authType: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="none">None</option>
                  <option value="apiKey">API Key</option>
                  <option value="bearer">Bearer Token</option>
                </select>
              </div>

              {newIntegration.authType === 'apiKey' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                  <input
                    type="password"
                    value={newIntegration.apiKey}
                    onChange={(e) => setNewIntegration(prev => ({ ...prev, apiKey: e.target.value }))}
                    placeholder="Enter API key..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              )}

              {newIntegration.authType === 'bearer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bearer Token</label>
                  <input
                    type="password"
                    value={newIntegration.bearerToken}
                    onChange={(e) => setNewIntegration(prev => ({ ...prev, bearerToken: e.target.value }))}
                    placeholder="Enter bearer token..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddIntegration}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Adding...' : 'Add Integration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowIntegrations;

